import { Injectable, signal, computed, inject } from '@angular/core';
import { MontajHesaplamaService } from './bin-packing.service';
import type { IStraforKutuk, IHedefParca, IMontajPlani } from '../interfaces/strafor.interface';
import type { KesimTipi, DetaySeviyesi, AnimasyonDurumu, IArtikParca } from '../interfaces/strafor.interface';
import { STANDART_KUTUKLER } from '../interfaces/strafor.interface';

@Injectable({ providedIn: 'root' })
export class StraforKesiciService {

  private montajServis = inject(MontajHesaplamaService);

  // ── State ─────────────────────────────────────
  kutukler = signal<IStraforKutuk[]>(STANDART_KUTUKLER.map(k => ({ ...k })));

  hedefParca = signal<IHedefParca>({ en: 2500, boy: 1500, yukseklik: 300 });

  secenekler = signal<IMontajPlani[]>([]);

  seciliSecenekIdx = signal(0);

  hesaplaniyor = signal(false);

  // Görünüm ayarları
  yapistamaGoster = signal(false);
  kesimCizgileriGoster = signal(false);
  patlatilmisGorunum = signal(false);

  // Geçici kütük öncelik modu
  geciciOncelikli = signal(false);

  // Montaj animasyonu
  montajAnimAktif = signal(false);
  montajAnimAdim = signal(0);
  montajAnimToplam = signal(0);
  montajAnimAciklama = signal('');
  montajAnimOtomatik = signal(false);
  montajIlerleme = signal(0); // 0-100 sürekli ilerleme

  // ═══ Kesim Animasyonu State ═══
  kesimAnimAktif = signal(false);
  kesimAnimDurum = signal<AnimasyonDurumu>('bekliyor');
  kesimTipi = signal<KesimTipi>('duzlem');
  detaySeviyesi = signal<DetaySeviyesi>('orta');
  hizCarpani = signal(1);
  kesimMevcutAdim = signal(0);
  kesimToplamAdim = signal(0);
  kesimIlerleme = signal(0);
  adimModu = signal(false);
  artikListesi = signal<IArtikParca[]>([]);

  // ── Computed ──────────────────────────────────
  montajPlani = computed(() =>
    this.secenekler()[this.seciliSecenekIdx()] ?? null
  );

  standartKutukler = computed(() => this.kutukler().filter(k => !k.gecici));
  geciciKutukler = computed(() => this.kutukler().filter(k => k.gecici));

  toplamKutukAdet = computed(() =>
    this.kutukler().reduce((t, k) => t + k.adet, 0)
  );

  // ── Hesaplama ─────────────────────────────────
  hesapla() {
    const standart = this.standartKutukler();
    const gecici = this.geciciKutukler();
    const hedef = this.hedefParca();
    if (standart.length === 0 || hedef.en <= 0 || hedef.boy <= 0 || hedef.yukseklik <= 0) return;

    this.hesaplaniyor.set(true);

    try {
      const sonuclar = this.montajServis.hesapla(hedef, standart, gecici, this.geciciOncelikli());
      this.secenekler.set(sonuclar);
      this.seciliSecenekIdx.set(0);
    } finally {
      this.hesaplaniyor.set(false);
    }
  }

  // Seçenek değiştir
  secenekSec(idx: number) {
    if (idx >= 0 && idx < this.secenekler().length) {
      this.seciliSecenekIdx.set(idx);
    }
  }

  // ── Hedef güncelle ────────────────────────────
  hedefGuncelle(veri: Partial<IHedefParca>) {
    this.hedefParca.update(h => ({ ...h, ...veri }));
    this.secenekler.set([]);
    this.seciliSecenekIdx.set(0);
  }

  // ── Kütük CRUD ────────────────────────────────
  kutukEkle(kutuk: IStraforKutuk) {
    this.kutukler.update(liste => [...liste, kutuk]);
    this.secenekler.set([]);
  }

  kutukSil(id: string) {
    this.kutukler.update(liste => liste.filter(k => k.id !== id));
    this.secenekler.set([]);
  }

  kutukGuncelle(id: string, veri: Partial<IStraforKutuk>) {
    this.kutukler.update(liste =>
      liste.map(k => k.id === id ? { ...k, ...veri } : k)
    );
    this.secenekler.set([]);
  }

  standartKutukleriYukle() {
    this.kutukler.set(STANDART_KUTUKLER.map(k => ({ ...k })));
    this.secenekler.set([]);
  }

  // ── Demo ──────────────────────────────────────
  ornekVeriYukle() {
    this.kutukler.set(STANDART_KUTUKLER.map(k => ({ ...k })));
    this.hedefParca.set({ en: 2500, boy: 1500, yukseklik: 300 });
    this.secenekler.set([]);
  }

  // ═══ Kesim Animasyonu Metotları ═══

  kesimAnimasyonuBaslat() {
    this.kesimAnimAktif.set(true);
    this.kesimAnimDurum.set('oynuyor');
    this.kesimMevcutAdim.set(0);
    this.kesimIlerleme.set(0);
  }

  kesimAnimasyonuDurdur() {
    this.kesimAnimAktif.set(false);
    this.kesimAnimDurum.set('bekliyor');
    this.kesimMevcutAdim.set(0);
    this.kesimIlerleme.set(0);
  }

  artikEkle(artik: IArtikParca) {
    this.artikListesi.update(liste => [...liste, artik]);
  }

  artikTemizle() {
    this.artikListesi.set([]);
  }

  // ── Temizle ───────────────────────────────────
  temizle() {
    this.hedefParca.set({ en: 2500, boy: 1500, yukseklik: 300 });
    this.secenekler.set([]);
    this.seciliSecenekIdx.set(0);
    this.yapistamaGoster.set(false);
    this.kesimCizgileriGoster.set(false);
    this.patlatilmisGorunum.set(false);
    this.geciciOncelikli.set(false);
    this.montajAnimAktif.set(false);
    this.montajAnimAdim.set(0);
    this.montajAnimToplam.set(0);
    this.montajAnimAciklama.set('');
    this.montajAnimOtomatik.set(false);
    this.montajIlerleme.set(0);
    this.kesimAnimasyonuDurdur();
    this.artikTemizle();
  }

  // ── Yardımcı ──────────────────────────────────
  yeniId(onek = 'item'): string {
    return `${onek}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  }
}
